import React, {
    useEffect,
    useState,
} from 'react';
import styled from 'styled-components';

import BREAKPOINTS from '@mlb/react-patterns/style/breakpoints.ts';
import COLORS from '@mlb/react-patterns/style/universal.color';
import { media } from '@mlb/react-patterns/style/helpers/media.ts';
import { Label } from '@mlb/react-patterns/components/Button/button.style.tsx';
import { IconWrapper } from '@mlb/react-patterns/components/Icon/icon.style.tsx';

import { useMedia } from 'bdvp-common/src/hooks';
import { useInAppView } from 'bdvp-common/src/hooks/config';
import Reels from 'bdvp-reel/src/containers/Reels';
import ReelsButton from 'bdvp-reel/src/components/ReelsButton';
import { FilterButton as FB } from 'bdvp-advanced-search';

import Menu from '../../containers/Menu';
import SearchButton from '../SearchButton';
import SearchInput from '../../containers/SearchInput';
import Branding from '../Branding';

const BaseSubnav = styled.nav`
    position: sticky;
    top: 0;
    height: ${({ theme }) => theme.subnav.height};
    background-color: ${COLORS.mlbGrey7};
    color: ${COLORS.mlbWhite};
    border-bottom: 1px solid ${COLORS.mlbGrey6};
    z-index: 10000;
    display: grid;
    grid-template-areas:
        'branding search menureels';
    grid-template-rows: 1fr;
    grid-template-columns: 1fr 1fr;
    align-items: center;
`;

const Navbar = styled(BaseSubnav)`
    ${media.mdmin`
        grid-template-areas: 'branding search menureels';
        grid-template-columns: 1fr 1.5fr 1fr;
    `}
    ${media.xlmin`
        grid-template-columns: 1fr 2fr 1fr;
    `}
`;

const FreeTextNavbar = styled(BaseSubnav)`
    display: flex;
    align-items: center;
    padding: ${({ theme }) => `0 6px 0 ${theme.pageTemplate.paddingLeftRight.xs}`};
`;

const BrandingWrapper = styled.div`
    grid-area: branding;
    justify-self: self-start;
    white-space: nowrap;
    align-self: center;
    ${media.mdmin`
        justify-self: self-start;
    `}
`;

const SearchButtonWrapper = styled.div`
    grid-area: search;
    justify-self: flex-end;

    ${media.mdmin`
        display: flex;
        gap: 8px;
        justify-self: center;
        width: calc(100% - 40px);
        margin-left: 40px;
    `}

    ${media.lgmin`
        width: 100%;
        margin-left: 0px;
    `}
`;

const SearchButtonOverride = styled(SearchButton)`
    font-size: 15px;

    ${Label} {
        display: none;

        ${media.lgmin`
            display: inline-block
        `}
    }
`;

const FilterButton = styled(FB).attrs({
    iconName: 'filter',
    iconSize: 18,
    iconLeft: false,
    shape: 'hollow',
    styleName: 'default',
    size: 'md',
    hasHover: false,
    labelKey: 'filters',
})`
    white-space: nowrap;

    ${Label} {
        display: none;
        margin-right: 8px;

        ${media.lgmin`
            display: inline-block
        `}
    }

    ${IconWrapper} {
        padding-top: 0;
        stroke: none;
    }
`;

const MenuWrapper = styled.div`
    grid-area: menu;
    justify-self: center;
    ${media.lgmin`
        display: flex;
        flex-direction: row;
        &::after {
            content: '';
            display: inline-block;
            width: 1px;
            height: 18px;
            margin: 0 10px;
            align-self: center;
            background-color: ${COLORS.mlbGrey6};
        }
    `}
`;

export const MenuReels = styled.div`
    display: grid;
    grid-area: menureels;
    grid-template-areas: ${({ showMenu }) => `'${showMenu ? 'menu reel' : 'reel'}'`};
    grid-template-rows: 1fr;
    grid-template-columns: ${({ showMenu }) => `${showMenu ? '1fr 1fr' : '1fr'}`};
    padding-right: 4px;

    ${media.smmin`
        display: flex;
        flex-direction: row;
        justify-self: flex-end;
    `}

    ${media.lgmin`
        padding-right: 22px;
    `}

    ${ReelsButton} {
        margin-left: 5px;
        grid-area: reel;
        justify-self: flex-end;
        justify-content: center;

        ${Label} {
            display: none;
        }

        ${media.lgmin`
            margin-left: 0px;

            ${Label}  {
                display: inline-block
            }
        `}
    }
`;


const Subnav = ({ isFilterActive }) => {

    const isMobile = useMedia([`(min-width: ${BREAKPOINTS.md})`], [false], true);
    const [
        showMenu = true,
        showFTS = true,
    ] = useInAppView([
        'channels',
        'FTS',
    ]);

    const [
        showFreeTextInput,
        setShowFreeTextInput,
    ] = useState(false);

    const [
        hasMounted,
        setHasMounted,
    ] = useState(false);

    useEffect(() => {

        setHasMounted(true);

    }, []);

    const displayFTS = showFTS && hasMounted;

    return (
        isMobile && showFreeTextInput
            ? (
                <FreeTextNavbar>
                    <SearchInput
                        setShowFreeTextInput={setShowFreeTextInput}
                        filterButton={<FilterButton active={isFilterActive} />}
                    />
                </FreeTextNavbar>
            )
            : (
                <Navbar>
                    <BrandingWrapper>
                        <Branding />
                    </BrandingWrapper>
                    <SearchButtonWrapper>
                        {displayFTS && (
                            isMobile
                                ? <SearchButtonOverride onClick={() => setShowFreeTextInput(true)} />
                                : (
                                    <>
                                        <SearchInput />
                                        <FilterButton active={isFilterActive} />
                                    </>
                                )
                        )}
                    </SearchButtonWrapper>
                    <MenuReels showMenu={showMenu}>
                        {showMenu && (
                            <MenuWrapper>
                                <Menu />
                            </MenuWrapper>
                        )}
                        <Reels />
                    </MenuReels>
                </Navbar>
            )
    );

};

export default Subnav;
